import '@app/App.css';
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ObjectDto } from "@dto/objects";
import User, { FullRightAccess, ReadRightAccess, hasGlobalRightAccess, ViewRightType } from '@dto/Users';
import { TransferViewOwnerRequest, View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_ViewMenuItem, TW_ViewMenuItemIcon, useHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Share2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useAppStore, useModelStore, useUserStore } from "src/store";
import ViewSharingEditor from "./ViewSharingEditor";

type DialogMode = 'share' | 'transfer-owner';
const SHARE_DIALOG_MODE: DialogMode = 'share';
const TRANSFER_OWNER_DIALOG_MODE: DialogMode = 'transfer-owner';

interface ViewMenuShareItemProps {
  object: ObjectDto;
  view: View;
  onViewUpdate: (view: View) => void;
  closeMenu: () => void;
}


function ViewMenuShareItem({ object, view, onViewUpdate, closeMenu }: ViewMenuShareItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(SHARE_DIALOG_MODE);
  const [updatedView, setUpdatedView] = useState<View>(view);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);

  const hasFullViewAccess = view.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, view._key);

  const viewUpdated = (view: View) => {
    setUpdatedView(view);
  }

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!hasFullViewAccess) {
      return;
    }
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      setDialogMode(SHARE_DIALOG_MODE);
      closeMenu();
      onViewUpdate(updatedView);
    }
  }

  function onOwnerTransferred(newView: View) {
    setUpdatedView(newView);
    onViewUpdate(newView);
    const isStillOwner = newView.ownerKey === user?._key;
    const hasSharedAccess = hasGlobalRightAccess(user, userRole, ViewRightType, ReadRightAccess, newView._key);
    if (!isStillOwner && !user?.owner && !hasSharedAccess) {
      // User lost ownership and has no remaining shared access — close dialog
      setIsDialogOpen(false);
    } else {
      setDialogMode(SHARE_DIALOG_MODE);
    }
  }

  const disabled = view === null || !hasFullViewAccess;
  const canChangeOwnership = user?.owner || view.ownerKey != null && view.ownerKey === user?._key;

  const dialogTitle = dialogMode === SHARE_DIALOG_MODE
    ? t("view.dialog.share.title") + " " + t("app.opening.quote") + view?.name + t("app.closing.quote")
    : t("view.dialog.transfer.owner.title") + " " + t("app.opening.quote") + view?.name + t("app.closing.quote");

  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_ViewMenuItem + ` ${disabled ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Share2 className={TW_ViewMenuItemIcon} />{t("view.button.share.text")}</DialogTrigger>
      <DialogContent className="w-full max-w-xl px-4">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription className="hidden">{t("object.dialog.share.description")}</DialogDescription>
        </DialogHeader>
        {view && dialogMode === SHARE_DIALOG_MODE && (
          <ViewSharingEditor
            view={updatedView}
            onClose={closeMenu}
            onViewUpdate={viewUpdated}
            onChangeOwner={canChangeOwnership ? () => setDialogMode(TRANSFER_OWNER_DIALOG_MODE) : undefined}
          />
        )}
        {view && dialogMode === TRANSFER_OWNER_DIALOG_MODE && (
          <ViewOwnerTransferForm
            view={updatedView}
            object={object}
            onTransferred={onOwnerTransferred}
            onCancel={() => setDialogMode(SHARE_DIALOG_MODE)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

interface ViewOwnerTransferFormProps {
  view: View;
  object: ObjectDto;
  onTransferred: (view: View) => void;
  onCancel: () => void;
}

function ViewOwnerTransferForm({ view, object, onTransferred, onCancel }: ViewOwnerTransferFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const allUsers = Array.from(useAppStore((state) => state.usersByKeys).values());
  const [selectedUserKey, setSelectedUserKey] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const selectableUsers = allUsers.filter((u: User) => u._key !== view.ownerKey);

  function onConfirm(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!selectedUserKey || !workspace?._key) {
      return;
    }

    const request: TransferViewOwnerRequest = { newOwnerKey: selectedUserKey };
    put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${object._key}/views/${view._key}/transfer-owner`, request).then((response) => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          setError(translate(t, cError.i18n, cError.params));
        } else {
          setError(cError.message);
        }
      } else {
        setError(null);
        onTransferred(response as View);
      }
    });
  }

  function onCancelClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="text-sm text-gray-600">{t("view.dialog.transfer.owner.label")}</p>
        <Select onValueChange={setSelectedUserKey} value={selectedUserKey}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("view.dialog.transfer.owner.placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {selectableUsers.map((u: User) => (
              <SelectItem key={u._key} value={u._key}>
                {u.firstname} {u.lastname} ({u.username ?? u._key})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {error && <div className={TW_ErrorMessage}>{error}</div>}
      {!error && <div className={TW_ErrorMessage}></div>}
      <div className={TW_ButtonList}>
        <Button className={TW_AppDialogButton} onClick={onCancelClick} {...cancelHover}>{t("general.button.cancel.text")}</Button>
        <Button className={TW_AppDialogButton} onClick={onConfirm} disabled={!selectedUserKey} {...okHover}>{t("general.button.confirm.text")}</Button>
      </div>
    </div>
  );
}

export default ViewMenuShareItem;
